/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useState } from 'react'
import { Button, Grid, TextField, Container, Typography, Alert, CircularProgress, Box, Link } from '@mui/material'
import './App.css'
import ConsultaTicketContext from './store/ConsultaTicketContext'
import { object, string } from 'zod'
import SearchIcon from '@mui/icons-material/Search'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

// Schema de validación para el formulario de consulta
const consultaSchema = object({
  numeroTicket: string().min(1, 'Debes ingresar el número de ticket'),
  verificacion: string().min(1, 'Debes ingresar tu RUT o correo electrónico')
})

// Tipo para la respuesta de la API
type ApiResponse = {
  encontrado: boolean
  retail?: string
  mensajes?: Array<{
    fecha: string
    mensaje: string
  }>
}

// Función que realiza una llamada a la API real
const callApi = async (numeroTicket: string, verificacion: string): Promise<ApiResponse | null> => {
  const apiUrl = process.env.REACT_APP_CONSULTA_TICKET_URL

  try {
    // Preparamos los parámetros para la solicitud
    const params = new URLSearchParams({
      numeroTicket,
      verificacion
    })

    // Realizamos la llamada a la API
    const response = await fetch(`${apiUrl}?${params.toString()}`)

    // Manejamos diferentes códigos de estado HTTP
    if (response.status === 404) {
      // Caso no encontrado
      return { encontrado: false }
    }

    if (!response.ok) {
      // Cualquier otro error HTTP
      console.error(`Error HTTP: ${response.status} ${response.statusText}`)
      throw new Error(`HTTP error! Status: ${response.status}`)
    }

    // Procesamos la respuesta exitosa
    const data = await response.json()
    return data as ApiResponse

  } catch (error: unknown) {
    // Registramos el error para depuración
    console.error('Error al llamar a la API:', error)

    // Para cualquier error de red o procesamiento, devolvemos null
    return null
  }
}

export function Consulta() {
  const context = useContext(ConsultaTicketContext)
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<ApiResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Validación del formulario
  const validaConsulta = () => {
    if (!context) return false

    const resul = consultaSchema.safeParse({
      numeroTicket: context.consulta.numeroTicket || '',
      verificacion: context.consulta.verificacion || ''
    })
    return resul.success
  }

  // Función para manejar la consulta
  const handleConsulta = async () => {
    if (!context) return

    setLoading(true)
    setResponse(null)
    setError(null)
    try {
      const result = await callApi(
        context.consulta.numeroTicket,
        context.consulta.verificacion
      )

      setResponse(result)

      // Si no hay resultado, mostramos un error genérico
      if (result === null) {
        setError('Ocurrió un error al procesar la consulta. Por favor, intente nuevamente mas tarde.')
      }
    } catch (err) {
      console.error('Error al consultar:', err)
      setError('Ocurrió un error inesperado. Por favor, intente nuevamente.')
    } finally {
      setLoading(false)
    }
  }


  // Función para manejar volver
  const handleVolver = async () => {
    if (!context) return

    setLoading(false)
    setResponse(null)
    setError(null)
    context.updateField('numeroTicket','')
    context.updateField('verificacion','')
  }

  const retail = response?.retail || null

  return (
    <Container maxWidth="sm" className='container'>
      <div className='inside-container'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" component="h1" gutterBottom>
              Consulta de Ticket
            </Typography>
            <Typography variant="body1" gutterBottom>
              Ingresa el número de ticket y tu RUT o correo electrónico registrado en tu ticket.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              size="small"
              id="numeroTicket"
              label="Número de Ticket"
              name="numeroTicket"
              sx={{ bgcolor: '#f5f5f5' }}
              onChange={context?.handleInput}
              value={context?.consulta.numeroTicket || ''}
              inputProps={{ autoComplete: 'off' }}
              disabled={response !== null && response.encontrado}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              size="small"
              id="verificacion"
              label="RUT o Correo Electrónico"
              name="verificacion"
              sx={{ bgcolor: '#f5f5f5' }}
              onChange={context?.handleInput}
              inputProps={{ autoComplete: 'off' }}
              value={context?.consulta.verificacion || ''}
              disabled={response !== null && response.encontrado}
            />
          </Grid>

          {/* Mostrar indicador de carga */}
          {loading && (
            <Grid item xs={12} style={{ textAlign: 'center', marginTop: '20px' }}>
              <CircularProgress color="primary" />
              <Typography variant="body2" style={{ marginTop: '10px' }}>
                Consultando información...
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} container style={{marginTop: '5px', marginBottom: '5px'}}>
            <Grid item xs={3}>
            </Grid>
            {(!response || !response.encontrado) ?
              (
                <Grid item xs={6}>
                  <Button
                    style={{borderRadius: '10px'}}
                    className='buttom-continue'
                    variant="contained"
                    color="primary"
                    disabled={!validaConsulta()}
                    onClick={handleConsulta}
                    endIcon={<SearchIcon />}>
                    Consultar
                  </Button>
                </Grid>
              ):(
                <Grid item xs={6}>
                  <Button
                    style={{borderRadius: '10px'}}
                    className='buttom-continue'
                    variant="contained"
                    color="primary"
                    onClick={handleVolver}
                    endIcon={<RestartAltIcon />}>
                    Volver
                  </Button>
                </Grid>
              )
            }
          </Grid>


          {/* Mostrar mensaje de error */}
          {error && (
            <Grid item xs={12} style={{ marginTop: '20px' }}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {/* Mostrar resultado de la consulta */}
          {response && (
            <Grid item xs={12} style={{ marginTop: '20px' }}>
              {response.encontrado ? (
                <>
                  <Alert severity="success">Información encontrada</Alert>

                  <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>Fecha</th>
                          <th style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>Mensaje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {response.mensajes && response.mensajes.map((msg, index) => (
                          (index === 0?<tr key={index}>
                            <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', fontSize: '0.85rem', fontWeight:900 }}>{msg.fecha.slice(0,19)}</td>
                            <td style={{ textAlign: 'justify', padding: '8px', borderBottom: '1px solid #eee', fontSize: '0.85rem', fontWeight:900}}>{msg.mensaje}</td>
                          </tr>:<tr key={index}>
                            <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', fontSize: '0.85rem' }}>{msg.fecha.slice(0,19)}</td>
                            <td style={{ textAlign: 'justify', padding: '8px', borderBottom: '1px solid #eee', fontSize: '0.85rem'}}>{msg.mensaje}</td>
                          </tr>)
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <Alert severity="warning">
                  No se encontró información para el ticket consultado.
                </Alert>
              )}
            </Grid>
          )}


          {response && response.encontrado && (
            <Grid item xs={12} container style={{marginTop: '5px', marginBottom: '10px'}}>
              <div style={{ textAlign: 'justify', padding: '15px'}}>
                En caso de dudas comunicarse a nuestro Call Center 600 600 0025 y será atendido por uno de nuestros (as) ejecutivos (as).<br />
                <br />
                Para envío de documentos y/o archivos (fotos, videos, entre otros) utilizar el siguiente correo electrónico:<br />
                <br />
                {
                  retail === 'UNIMARC' ? <><Box component="b">Unimarc:</Box> <Link href="mailto:contacto.unimarc@unimarc.cl">contacto.unimarc@unimarc.cl</Link></> :
                    retail === 'UNIMARC.CL' ? <><Box component="b">Unimarc:</Box> <Link href="mailto:contacto.unimarc@unimarc.cl">contacto.unimarc@unimarc.cl</Link></> :
                      retail === 'ALVI' ? <><Box component="b">Alvi:</Box> <Link href="mailto:contacto.alvi@alvi.cl">contacto.alvi@alvi.cl</Link></> :
                        retail === 'M10' ? <><Box component="b">Mayorista10:</Box> <Link href="mailto:contacto.super10@super10.cl">contacto.super10@super10.cl</Link></> :
                          retail === 'SUPER 10' ? <><Box component="b">Super10:</Box> <Link href="mailto:contacto.super10@super10.cl">contacto.super10@super10.cl</Link></> :
                            <><Box component="b">Unimarc:</Box> <Link href="mailto:contacto.unimarc@unimarc.cl">contacto.unimarc@unimarc.cl</Link></>
                }
              </div>
            </Grid>
          )}
        </Grid>
      </div>
    </Container>
  )
}
